import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  checkSchedule,
  largeOrderShopperWords,
  needsVehicle,
  postcodeIn,
  runnerPaymentFor,
  scheduleAgreementWords,
  ukDay,
} from '@aldilivery/core';

import { moneyIn, moneyOut } from '../lib/alert';

import { NoRunnerWarning } from '../components/NoRunnerWarning';
import { leftCheckout } from '../components/WhyStopped';
import { NotifyMe } from '../components/NotifyMe';
import { OverOneRunner } from '../components/OverOneRunner';
import { PinGate } from '../components/PinGate';
import { startingBooking, WhenItComes, type Booking } from '../components/WhenItComes';
import { storeConfig } from '../config';
import {
  checkArea,
  createOrder,
  fetchBankTransferEnabled,
  fetchAddresses,
  listPaymentMethods,
  markAreaWarned,
  priceBasketRemotely,
  recordCheckoutViewed,
  saveAddress,
  updateMe,
  type AddressBook,
  type PaymentMethod,
  type PlacedOrder,
} from '../lib/api';
import { deliveryWords, money } from '../lib/money';
import { orderReference } from '../lib/order-reference';
import { prepareCardEntry } from '../lib/stripe';
import { useBasket } from '../state/basket';
import { useOzi } from '../state/ozi';
import { useSession } from '../state/session';

/**
 * The confirmation screen. Rule One made visible, and now real.
 *
 * There is exactly one button on this page that could ever lead to a payment, it is the
 * largest thing on the screen, and it says what it does. Everything above it is a plain
 * statement of what will happen and what it will cost. Nothing is pre-ticked, nothing is
 * assumed, and there is no second way to confirm hidden anywhere.
 *
 * The sentence next to the button is not decoration. It is sent to the server as the
 * `statement` on the confirmation and stored on the order, so what the Shopper was told they
 * were agreeing to is on the record next to the charge. The total is sent with it, and the
 * server prices the basket again from its own catalogue and refuses the whole order if the
 * figure has moved. A price that changed while somebody was deciding is a reason to ask
 * again, not a reason to charge a different amount.
 *
 * Every figure is on this page before the button: the shopping at shop prices, the item charges,
 * delivery or the restaurant fee, any extra delivery charge, and the total (Anthony, 30 October
 * 2026, checked for the checkout insights). Reaching it is recorded with that breakdown, the kind
 * of order, the area, the no-Runner warning and new or returning; leaving it without paying lets
 * "Can we ask what stopped you?" be asked, once a day at most (components/WhyStopped.tsx).
 */
export function Confirm(): JSX.Element {
  const basket = useBasket();
  const {
    lines,
    overMaximum,
    overOneRunner,
    parts,
    clear,
    restaurant,
    restaurantProblem,
    orderNote,
  } = basket;
  /**
   * When it comes, and when the money is taken (ruling 78). Today unless the Shopper chooses a
   * later day. A day in another month counts towards that month's allowance (ruling 73), so the
   * server prices it for that month and the figure agreed to is the one charged.
   */
  const [booking, setBooking] = useState<Booking>(() => startingBooking());
  const later = booking.when === 'later';
  const otherMonth = later && booking.deliveryDay.slice(0, 7) !== ukDay(new Date()).slice(0, 7);
  const [monthPrice, setMonthPrice] = useState<{
    day: string;
    allowanceExtraPence: number;
    totalPence: number;
  } | null>(null);
  // If that price could not be worked out, say so and try again on the next press, rather than
  // waiting for ever (30 October 2026: a booking for next month stuck on "One moment").
  const [monthFailed, setMonthFailed] = useState(false);
  const [monthTry, setMonthTry] = useState(0);
  useEffect(() => {
    if (!otherMonth) return;
    let cancelled = false;
    setMonthFailed(false);
    priceBasketRemotely(
      lines.map((line) => ({ catalogueItemId: line.item.id, quantity: line.quantity })),
      booking.deliveryDay,
    )
      .then((price) => {
        if (cancelled) return;
        setMonthPrice({
          day: booking.deliveryDay,
          allowanceExtraPence: price.allowanceExtraPence ?? 0,
          totalPence: price.totalPence,
        });
      })
      .catch(() => {
        if (!cancelled) setMonthFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [otherMonth, booking.deliveryDay, lines, monthTry]);
  const monthApplies = otherMonth && monthPrice?.day === booking.deliveryDay;
  const pricing = monthApplies
    ? {
        ...basket.pricing,
        allowanceExtraPence: monthPrice.allowanceExtraPence,
        totalPence: monthPrice.totalPence,
      }
    : basket.pricing;
  const allowanceWords = monthApplies ? null : basket.allowanceWords;
  /** Ruling 61: over £150, the Shopper chooses to keep everything. Never ticked for them. */
  const [keepEverything, setKeepEverything] = useState(false);
  const { shopper, restoring, replaceShopper, refresh } = useSession();

  const [cards, setCards] = useState<PaymentMethod[] | null>(null);
  const [placed, setPlaced] = useState<{ order: PlacedOrder; message: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  /** The order sent was booked for a later day (ruling 78). */
  const [booked, setBooked] = useState(false);

  const [editingAddress, setEditingAddress] = useState(false);
  const [address, setAddress] = useState('');
  /**
   * Where it can go (docs/BUILD_PROMPT.md, Section D): the home address first, then any saved,
   * then "send to a different address", which asks afterwards whether to save it (with the
   * PIN) or use it this once (no PIN), the way a bank app asks about a payee.
   */
  const [book, setBook] = useState<AddressBook | null>(null);
  const [chosen, setChosen] = useState<string>('home');
  const [oneOff, setOneOff] = useState('');
  const [other, setOther] = useState<'closed' | 'typing' | 'asking' | 'saving'>('closed');
  const [typed, setTyped] = useState('');
  /**
   * The address the Shopper has said is right (docs/BUILD_PROMPT.md, Section D: before every
   * order, of every type, the actual address is put to them and they say yes). Changing the
   * address means saying yes again.
   */
  const [confirmedAddress, setConfirmedAddress] = useState<string | null>(null);
  const addressConfirmed = confirmedAddress !== null && confirmedAddress === address.trim();
  /**
   * Every area is open (Anthony, 30 October 2026). Where the address this order goes to has no
   * approved Runner yet, or its area is paused, a gentle warning is shown as an alert dialog once
   * the address is confirmed, once for each order attempt (each address), and said by Ozi. It
   * never stops the order: "OK, I understand" goes back to it, and the button to pay is never
   * greyed out because of it. Asked as soon as the address is shown; shown nowhere else.
   */
  const [noRunner, setNoRunner] = useState(false);
  /** The area's answer is in (or there is no postcode to ask about): for the checkout insights. */
  const [areaKnown, setAreaKnown] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const warnedFor = useRef<string | null>(null);
  const payButton = useRef<HTMLButtonElement | null>(null);
  const [areaNews, setAreaNews] = useState('');
  const postcode = postcodeIn(address);
  useEffect(() => {
    setNoRunner(false);
    if (postcode === '') {
      setAreaKnown(true);
      return undefined;
    }
    let cancelled = false;
    checkArea(postcode)
      .then((result) => {
        if (!cancelled && result.warn === true) setNoRunner(true);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setAreaKnown(true);
      });
    return () => {
      cancelled = true;
    };
  }, [postcode]);
  const ozi = useOzi();
  /** Shows the warning, unless it was shown already for this address. True when it opened. */
  const warnOnce = useCallback(
    (where: string): boolean => {
      if (!noRunner || where === '' || warnedFor.current === where) return false;
      warnedFor.current = where;
      setWarningOpen(true);
      void ozi.say(storeConfig.areas.noRunnerWords);
      // So they are texted once when a Runner joins there. Nothing waits for it.
      markAreaWarned(where).catch(() => undefined);
      return true;
    },
    [noRunner, ozi],
  );
  useEffect(() => {
    if (addressConfirmed) warnOnce(address.trim());
  }, [addressConfirmed, address, warnOnce]);
  const spokenFor = useRef<string | null>(null);

  const home = book?.home ?? shopper?.deliveryAddress ?? '';
  useEffect(() => {
    if (chosen === 'home') setAddress(home);
    else if (chosen === 'once') setAddress(oneOff);
    else setAddress(book?.saved.find((saved) => saved.id === chosen)?.address ?? home);
  }, [chosen, home, oneOff, book]);

  useEffect(() => {
    if (!shopper) return;
    let cancelled = false;
    fetchAddresses()
      .then((found) => {
        if (!cancelled) setBook(found);
      })
      .catch(() => {
        // Without the list, the home address on the account is still there to send to.
      });
    return () => {
      cancelled = true;
    };
  }, [shopper]);

  // Ozi says the address aloud, once for each address shown, and waits for the yes below.
  const askAboutAddress =
    shopper !== null && lines.length > 0 && !placed && !editingAddress && other === 'closed';
  useEffect(() => {
    const where = address.trim();
    if (!askAboutAddress || where === '' || addressConfirmed || spokenFor.current === where) return;
    spokenFor.current = where;
    void ozi.say(
      `Before you send it: this order will go to ${where}. If that is right, press Yes, this is the right address.`,
    );
  }, [askAboutAddress, address, addressConfirmed, ozi]);

  useEffect(() => {
    if (!shopper) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await listPaymentMethods();
        if (!cancelled) setCards(result.paymentMethods);
      } catch {
        // Treated as "no card we can see" rather than an error of its own. The screen below
        // already has something sensible to say when there is no card.
        if (!cancelled) setCards([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopper]);

  // Paying by bank transfer to the business account, when switched on (ruling 50).
  const [bankOn, setBankOn] = useState(false);
  const [payBy, setPayBy] = useState<'card' | 'bank'>('card');
  useEffect(() => {
    let cancelled = false;
    fetchBankTransferEnabled()
      .then((on) => {
        if (!cancelled) setBankOn(on);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Ozi Family and Carer: a member's order is paid with the payer's card (ruling 58).
  const byFamily = Boolean(shopper?.familyOwnerId) && shopper?.activePlan === 'family';
  const extraDeliveryPence = parts.reduce((sum, part) => sum + (part.extra ? part.feePence : 0), 0);
  // Past the plan's monthly allowance (ruling 73): the extra delivery charge is in the words
  // agreed to, as its own figure.
  const allowanceExtra =
    pricing.allowanceExtraPence > 0
      ? `, and an extra delivery charge of ${money(pricing.allowanceExtraPence)}`
      : '';
  const choice = {
    deliveryDay: booking.deliveryDay,
    window: booking.window,
    chargePlan: byFamily && booking.pay !== 'now' ? ('now' as const) : booking.pay,
    chargeDay: booking.chargeDay,
    chargeTime: booking.chargeTime,
  };
  // The booking, said plainly in the words agreed to (ruling 78): the day and window, when the
  // money is taken, and that the final amount is what the till says on the day.
  const bookingWords = later ? ` ${scheduleAgreementWords(choice, storeConfig.scheduled)}` : '';
  const baseStatement = overOneRunner
    ? `Send my order and pay. About ${money(pricing.totalPence)} altogether, as ${parts.length} orders each with its own Runner: the shopping about ${money(pricing.goodsPence + pricing.itemChargesPence)} with item charges of ${money(pricing.itemChargesPence)} included, and delivery ${money(pricing.feePence)}, of which ${money(extraDeliveryPence)} for further Runners is ${bankOn && payBy === 'bank' && !(Boolean(shopper?.familyOwnerId) && shopper?.activePlan === 'family') ? 'given back as credit if they are not needed' : 'taken only when each collects'}${allowanceExtra}.`
    : restaurant
      ? `Send my order and pay. About ${money(pricing.totalPence)} altogether: the dishes from ${restaurant.name} about ${money(pricing.goodsPence + pricing.itemChargesPence)} with item charges of ${money(pricing.itemChargesPence)} included, and the restaurant fee ${money(pricing.feePence)}, in place of delivery.`
      : `Send my order and pay. About ${money(pricing.totalPence)} altogether: the shopping about ${money(pricing.goodsPence + pricing.itemChargesPence)} with item charges of ${money(pricing.itemChargesPence)} included, and delivery ${money(pricing.feePence)}${allowanceExtra}.`;
  const statement = `${baseStatement}${bookingWords}`;

  /**
   * Checkout insights (30 October 2026): this checkout is recorded once, when the page has the
   * Shopper, the basket and the area's answer, with everything it shows. Never a card detail.
   */
  const viewedOnce = useRef(false);
  const shown = useRef({ pricing, restaurant, address, noRunner });
  shown.current = { pricing, restaurant, address, noRunner };
  useEffect(() => {
    if (viewedOnce.current || !shopper || lines.length === 0 || placed || !areaKnown) return;
    viewedOnce.current = true;
    const now = shown.current;
    recordCheckoutViewed({
      goodsPence: now.pricing.goodsPence,
      itemChargesPence: now.pricing.itemChargesPence,
      feePence: now.pricing.feePence + now.pricing.allowanceExtraPence,
      totalPence: now.pricing.totalPence,
      restaurant: now.restaurant !== null,
      area: now.address.trim(),
      noRunnerWarning: now.noRunner,
      channel: 'web',
    }).catch(() => undefined);
  }, [shopper, lines.length, placed, areaKnown]);

  // Left without paying, or back to the basket: the small question may be asked, once a day.
  const leaving = useRef({ placed: false });
  leaving.current.placed = placed !== null;
  useEffect(
    () => () => {
      if (!viewedOnce.current || leaving.current.placed) return;
      const now = shown.current;
      leftCheckout({
        restaurant: now.restaurant !== null,
        totalPence: now.pricing.totalPence,
        area: now.address.trim(),
      });
    },
    [],
  );

  // A restaurant order that cannot go as it is (30 October 2026) is said by Ozi, once.
  const restaurantSaid = useRef<string | null>(null);
  useEffect(() => {
    if (restaurantProblem && restaurantSaid.current !== restaurantProblem) {
      void ozi.say(restaurantProblem);
    }
    restaurantSaid.current = restaurantProblem;
  }, [restaurantProblem, ozi]);

  /**
   * The button is never greyed out (review of 16 October 2026): a disabled button says nothing
   * about why. Pressed with something missing, the page shows, and Ozi says, exactly what.
   */
  function whatIsMissing(): string | null {
    const card = cards?.find((method) => method.isDefault) ?? cards?.[0];
    const byBank = payingByBank();
    // A restaurant order (30 October 2026): never mixed, and never below the smallest order.
    if (restaurantProblem) return restaurantProblem;
    if (overMaximum) {
      return `Your shopping is more than one basket holds, ${money(storeConfig.fees.maximumBasketGoodsPence)}. Please go back to your basket and take some things out.`;
    }
    if (address.trim() === '' || !addressConfirmed) return 'Please confirm the address first';
    if (!card && !byBank && !byFamily) return 'Please add a card or choose bank transfer';
    if (overOneRunner && !keepEverything) {
      return 'Please tick "Keep everything", or go back to your basket to take something out.';
    }
    if (later) {
      if (overOneRunner) {
        return 'A basket for more than one Runner can only be sent for as soon as possible at the moment. Please choose as soon as possible, or take something out.';
      }
      const check = checkSchedule(choice, new Date(), storeConfig.scheduled);
      if (!check.ok) return check.message;
      if (otherMonth && !monthApplies) {
        return monthFailed
          ? 'We could not work out the price for that month. Please press the button again to try once more.'
          : 'One moment: working out the price for that month.';
      }
    }
    return null;
  }

  /** By bank transfer: chosen for today in "Pay by", or for a later day in "When it comes". */
  function payingByBank(): boolean {
    if (byFamily) return false;
    if (later) return bankOn && booking.pay === 'bank';
    return bankOn && payBy === 'bank';
  }

  async function onSend(): Promise<void> {
    if (sending) return;
    const card = cards?.find((method) => method.isDefault) ?? cards?.[0];
    const byBank = payingByBank();
    const missing = whatIsMissing();
    if (missing !== null) {
      // The price for next month could not be had: this press asks again.
      if (later && otherMonth && !monthApplies && monthFailed) setMonthTry((n) => n + 1);
      setError(missing);
      void ozi.say(missing);
      return;
    }
    // The warning comes before paying, once, if it has not been shown yet for this address (the
    // area's answer came after the address was confirmed). The next press pays.
    if (warnOnce(address.trim())) return;

    setSending(true);
    setError('');
    try {
      const result = await createOrder({
        lines: lines.map((line) => ({
          catalogueItemId: line.item.id,
          quantity: line.quantity,
          ...(line.note ? { note: line.note } : {}),
        })),
        ...(orderNote.trim() ? { note: orderNote.trim() } : {}),
        deliveryAddress: address.trim(),
        ...(byFamily
          ? { payBy: 'family' as const }
          : byBank || !card
            ? { payBy: 'bank' as const }
            : { paymentMethodId: card.id }),
        ...(overOneRunner ? { keepEverything: true } : {}),
        ...(later
          ? {
              schedule: {
                deliveryDay: booking.deliveryDay,
                window: booking.window,
                pay: byBank || !card ? ('bank' as const) : choice.chargePlan,
                ...(choice.chargePlan === 'scheduled'
                  ? { chargeDay: booking.chargeDay, chargeTime: booking.chargeTime }
                  : {}),
              },
            }
          : {}),
        confirmation: { statement, agreedTotalPence: pricing.totalPence, addressConfirmed: true },
      });
      setBooked(later);
      // The monthly order count (ruling 73) is the server's: asked again after every order.
      void refresh();
      if (result.bank || result.scheduled) {
        clear();
        setPlaced({ order: result.order, message: result.message });
        return;
      }

      /**
       * A card in the United Kingdom usually has to be authenticated by the Shopper's bank.
       * When it does, the server leaves the order unpaid and hands back a client secret, and
       * this is where the bank's own screen is opened. Stripe tells us how it went, and the
       * order is settled by the webhook rather than by anything we could claim here.
       */
      if (result.payment?.requiresAction && result.payment.clientSecret) {
        const setup = await prepareCardEntry();
        if (setup.ready) {
          const outcome = await setup.stripe.handleNextAction({
            clientSecret: result.payment.clientSecret,
          });
          if (outcome.error) {
            setError(
              outcome.error.message ??
                'Your bank did not approve the payment. Nothing has been taken, and your order has not been sent.',
            );
            return;
          }
        }
      }

      clear();
      // A till's "ka-ching": the money has gone. And coins back, if gift card money returned.
      // Nothing has gone yet when the family plan's payer must approve it first.
      if (!result.waitingForApproval) moneyOut();
      if (result.order.creditAppliedPence) window.setTimeout(moneyIn, 700);
      setPlaced({ order: result.order, message: result.message });
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Something went wrong. Nothing has been charged.',
      );
    } finally {
      setSending(false);
    }
  }

  /** The very first home address, given here without a PIN as it would be at sign up. */
  async function onSaveAddress(): Promise<void> {
    try {
      const result = await updateMe({ deliveryAddress: address.trim() });
      replaceShopper(result.shopper);
      setBook((previous) =>
        previous ? { ...previous, home: result.shopper.deliveryAddress } : previous,
      );
      setEditingAddress(false);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'We could not save that address.');
    }
  }

  /* ---------------------------------------------------------------------------------- *
   * The states that come before the button
   * ---------------------------------------------------------------------------------- */

  if (placed && booked) {
    return (
      <div className="space-y-6">
        <h1 className="text-display font-bold m-0">Your order is booked</h1>
        <p role="status" className="m-0 max-w-xl text-lead">
          {placed.message}
        </p>
        <p className="m-0 max-w-xl">
          We will remind you the day before and on the morning, and a Runner is found on the day.
          You can change or cancel it under Past orders until then.
        </p>
        <NotifyMe />
        <p className="m-0 text-paper/80">
          Your order reference is {orderReference(placed.order.id)}.
        </p>
        <Link to="/orders" className="control bg-highlight text-on-accent">
          See your booked orders
        </Link>
        <Link to="/" className="control bg-paper/10 text-paper underline">
          Back to the start
        </Link>
      </div>
    );
  }

  if (placed) {
    return (
      <div className="space-y-6">
        <h1 className="text-display font-bold m-0">Your order is sent</h1>
        <p role="status" className="m-0 max-w-xl text-lead">
          {placed.message}
        </p>
        <p className="m-0 max-w-xl">
          We are finding you a Runner now. They will have your doorstep instructions exactly as you
          wrote them.
        </p>
        <p className="m-0 max-w-xl">
          If your Runner cannot find something, they will ask you on your order page, and you choose
          what they do. Nobody is given your phone number.
        </p>
        <NotifyMe />
        <p className="m-0 text-paper/80">
          Your order reference is {orderReference(placed.order.id)}. Your Runner sees the same one.
        </p>
        <Link to="/my-order" className="control bg-highlight text-on-accent">
          Follow your order
        </Link>
        <Link to="/" className="control bg-paper/10 text-paper underline">
          Back to the start
        </Link>
      </div>
    );
  }

  if (restoring) {
    return (
      <p role="status" className="m-0">
        One moment.
      </p>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-display font-bold m-0">Send your order</h1>
        <p className="m-0">There is nothing in your basket yet.</p>
        <Link to="/shop" className="control bg-highlight text-on-accent">
          Find your shopping
        </Link>
      </div>
    );
  }

  if (!shopper) {
    return (
      <div className="space-y-6">
        <h1 className="text-display font-bold m-0">We need to know who you are</h1>
        <p className="m-0 max-w-xl">
          Your basket is safe. Setting up an account takes a moment, and then we can bring this to
          your door.
        </p>
        <Link to="/sign-up" className="control bg-highlight text-on-accent">
          Set up my account
        </Link>
        <Link to="/sign-in?next=/confirm" className="control bg-paper/10 text-paper underline">
          I already have an account: sign in
        </Link>
        <p className="m-0">
          <Link to="/basket" className="underline">
            Go back to my basket
          </Link>
        </p>
      </div>
    );
  }

  const card = cards?.find((method) => method.isDefault) ?? cards?.[0];
  const byBank = payingByBank();

  return (
    <div className="space-y-8">
      <h1 className="text-display font-bold m-0">Send your order</h1>

      <section aria-labelledby="what-heading" className="space-y-3">
        <h2 id="what-heading" className="text-lead font-bold">
          What you are asking for
        </h2>
        <ul className="list-none m-0 p-0 space-y-2">
          {lines.map((line) => (
            <li key={line.item.id} className="m-0">
              {line.quantity} × {line.item.name}
              {line.note ? ` (note: ${line.note})` : ''}
            </li>
          ))}
        </ul>
        {orderNote.trim() !== '' && <p className="m-0">Note for your Runner: {orderNote.trim()}</p>}
      </section>

      <section aria-labelledby="cost-heading" className="space-y-3">
        <h2 id="cost-heading" className="text-lead font-bold">
          What it will cost
        </h2>
        <p className="m-0">
          Your shopping, about {money(pricing.goodsPence + pricing.itemChargesPence)}, with the item
          charges included: {money(pricing.goodsPence)} at the shop&rsquo;s prices and{' '}
          {money(pricing.itemChargesPence)} of item charges.
        </p>
        {restaurant ? (
          <p className="m-0">
            Restaurant fee, {money(pricing.feePence)}, in place of delivery: a restaurant order from{' '}
            {restaurant.name}.
          </p>
        ) : (
          <p className="m-0">Delivery, {money(pricing.feePence)}.</p>
        )}
        {pricing.allowanceExtraPence > 0 && (
          <p className="m-0">Extra delivery charge, {money(pricing.allowanceExtraPence)}.</p>
        )}
        {allowanceWords && <p className="m-0 font-bold">{allowanceWords}</p>}
        <p className="m-0 text-lead font-bold">Altogether, about {money(pricing.totalPence)}.</p>
        {restaurantProblem && (
          <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
            {restaurantProblem}
          </p>
        )}
        <p className="m-0">
          {restaurant
            ? `The restaurant fee is the same on every plan. You pay ${restaurant.name}'s own prices for the dishes, so this may change a little if something is not available.`
            : `${deliveryWords(pricing.plan)} You pay what the till says for the shopping, so this may change a little.`}
          {!overOneRunner && (
            <>
              {' '}
              Your Runner gets {money(runnerPaymentFor(pricing.goodsPence, storeConfig.fees))} of
              the {restaurant ? 'restaurant fee' : 'delivery'}.
            </>
          )}
        </p>
        {overOneRunner && (
          <>
            <OverOneRunner parts={parts} />
            <div className="flex items-start gap-3 min-h-control">
              <input
                id="keep-everything"
                type="checkbox"
                checked={keepEverything}
                onChange={(event) => setKeepEverything(event.target.checked)}
                className="h-7 w-7 shrink-0"
              />
              <label htmlFor="keep-everything">
                Keep everything, as {parts.length} orders each with its own Runner
              </label>
            </div>
            <p className="m-0">
              <Link to="/basket" className="underline">
                Or take something out or swap it, to stay with one Runner
              </Link>
            </p>
          </>
        )}
        {byFamily && <p className="m-0">This is paid with your family plan&rsquo;s card.</p>}
        {needsVehicle(pricing.goodsPence, storeConfig.dispatch.maxGoodsPenceByMode) && (
          <p className="m-0">
            {largeOrderShopperWords(pricing.goodsPence, storeConfig.dispatch.maxGoodsPenceByMode)}
          </p>
        )}
      </section>

      <section aria-labelledby="where-heading" className="space-y-3">
        <h2 id="where-heading" className="text-lead font-bold">
          Where it is going
        </h2>
        {editingAddress ? (
          <div className="space-y-2 max-w-xl">
            <label htmlFor="deliveryAddress" className="block font-bold">
              Your home address
            </label>
            <textarea
              id="deliveryAddress"
              name="deliveryAddress"
              rows={3}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
            />
            <button
              type="button"
              onClick={() => {
                void onSaveAddress();
              }}
              className="control border-paper bg-paper/10 text-paper"
            >
              Save this address
            </button>
          </div>
        ) : other === 'typing' ? (
          <form
            className="space-y-2 max-w-xl"
            onSubmit={(event) => {
              event.preventDefault();
              if (typed.trim().length < 5) return;
              setOther('asking');
            }}
          >
            <label htmlFor="otherAddress" className="block font-bold">
              The address to send it to
            </label>
            <p id="otherAddress-hint" className="m-0 text-paper/90">
              The house number, street, town and postcode.
            </p>
            <textarea
              id="otherAddress"
              rows={3}
              value={typed}
              aria-describedby="otherAddress-hint"
              onChange={(event) => setTyped(event.target.value)}
              className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
            />
            <div className="flex flex-wrap gap-3">
              <button type="submit" className="control bg-highlight text-on-accent">
                Next
              </button>
              <button
                type="button"
                onClick={() => setOther('closed')}
                className="control bg-paper/10 text-paper underline"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : other === 'asking' ? (
          <div className="space-y-3 max-w-xl">
            <p className="m-0">{typed.trim()}</p>
            <p className="m-0 font-bold">Save it for next time, or use it this once?</p>
            <p className="m-0">Saving it needs your PIN. Using it once does not.</p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setOther('saving')}
                className="control border-paper bg-paper/10 text-paper"
              >
                Save it for next time
              </button>
              <button
                type="button"
                onClick={() => {
                  setOneOff(typed.trim());
                  setChosen('once');
                  setOther('closed');
                }}
                className="control border-paper bg-paper/10 text-paper"
              >
                Use it once, for this order only
              </button>
            </div>
          </div>
        ) : other === 'saving' ? (
          <div className="space-y-3">
            <p className="m-0">Saving {typed.trim()}</p>
            <PinGate
              hasPin={book?.hasPin ?? shopper.hasPin ?? false}
              setHasPin={(hasPin) => {
                setBook((previous) => (previous ? { ...previous, hasPin } : previous));
              }}
              purpose="to save this address"
              onCancel={() => setOther('asking')}
              action={async (pin) => {
                const result = await saveAddress({ address: typed.trim(), pin });
                setBook((previous) =>
                  previous ? { ...previous, saved: [...previous.saved, result.address] } : previous,
                );
                setChosen(result.address.id);
                setOther('closed');
              }}
            />
          </div>
        ) : (
          <>
            {(book?.saved.length ?? 0) > 0 || chosen === 'once' ? (
              <fieldset className="border-2 border-paper/40 rounded-2xl p-4 m-0 space-y-2 max-w-xl">
                <legend className="px-2 font-bold">Choose where it goes</legend>
                {[
                  { key: 'home', label: 'Your home address', where: home },
                  ...(book?.saved ?? []).map((saved) => ({
                    key: saved.id,
                    label: saved.label || 'Saved address',
                    where: saved.address,
                  })),
                  ...(oneOff !== ''
                    ? [{ key: 'once', label: 'This order only', where: oneOff }]
                    : []),
                ].map((option) => (
                  <div key={option.key} className="flex items-start gap-3 min-h-control">
                    <input
                      id={`where-${option.key}`}
                      type="radio"
                      name="where"
                      checked={chosen === option.key}
                      onChange={() => setChosen(option.key)}
                      className="h-7 w-7 mt-1 shrink-0"
                    />
                    <label htmlFor={`where-${option.key}`}>
                      <span className="font-bold">{option.label}:</span> {option.where}
                    </label>
                  </div>
                ))}
              </fieldset>
            ) : (
              <p className="m-0">
                {address === '' ? 'You have not given us an address yet.' : address}
              </p>
            )}
            {address.trim() !== '' &&
              (addressConfirmed ? (
                <p role="status" className="m-0 font-bold">
                  You said this is the right address.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmedAddress(address.trim())}
                  className="control bg-highlight text-on-accent"
                >
                  Yes, this is the right address
                </button>
              ))}
            {home === '' ? (
              <button
                type="button"
                onClick={() => setEditingAddress(true)}
                className="control border-paper bg-paper/10 text-paper"
              >
                Add an address
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setTyped('');
                  setOther('typing');
                }}
                className="control border-paper bg-paper/10 text-paper"
              >
                Send to a different address
              </button>
            )}
          </>
        )}
      </section>

      <WhenItComes
        booking={booking}
        onChange={setBooking}
        bankOn={bankOn}
        laterPaymentAllowed={!byFamily}
      />
      {later && (
        <p className="m-0 max-w-xl font-bold">
          {scheduleAgreementWords(choice, storeConfig.scheduled)}
        </p>
      )}

      <section aria-labelledby="card-heading" className="space-y-3">
        <h2 id="card-heading" className="text-lead font-bold">
          How you are paying
        </h2>
        {bankOn && !later && (
          <fieldset className="border-2 border-paper/40 rounded-2xl p-4 m-0 space-y-2">
            <legend className="px-2 font-bold">Pay by</legend>
            <div className="flex items-center gap-3 min-h-control">
              <input
                id="pay-card"
                type="radio"
                name="pay-by"
                checked={payBy === 'card'}
                onChange={() => setPayBy('card')}
                className="h-7 w-7 shrink-0"
              />
              <label htmlFor="pay-card">My card</label>
            </div>
            <div className="flex items-center gap-3 min-h-control">
              <input
                id="pay-bank"
                type="radio"
                name="pay-by"
                checked={payBy === 'bank'}
                onChange={() => setPayBy('bank')}
                aria-describedby="pay-bank-hint"
                className="h-7 w-7 shrink-0"
              />
              <label htmlFor="pay-bank">Bank transfer to us</label>
            </div>
            <p id="pay-bank-hint" className="m-0">
              We give you our bank details and a reference. A Runner is sent once your transfer
              arrives. A Faster Payments transfer usually arrives within minutes, and staff check
              for transfers at least every morning and evening. A refund to a bank transfer takes
              longer than to a card.
            </p>
          </fieldset>
        )}
        {byBank ? null : cards === null ? (
          <p role="status" className="m-0">
            Checking which card you have saved.
          </p>
        ) : card ? (
          <p className="m-0">
            Your {card.brand ?? 'card'} ending {card.lastFour}.
          </p>
        ) : (
          <>
            <p className="m-0">You have not saved a card yet, so there is nothing to pay with.</p>
            <Link to="/card" className="control border-paper bg-paper/10 text-paper">
              Add a card
            </Link>
          </>
        )}
      </section>

      <section aria-labelledby="promise-heading" className="space-y-3">
        <h2 id="promise-heading" className="text-lead font-bold">
          Before you press it
        </h2>
        {/* What saying yes covers, listed once, every figure from config (Rule Nine). The
            charges that follow stay automatic; the Shopper is told of each here, before they
            agree (Anthony, 30 October 2026). */}
        <p className="m-0">
          We never take money you haven&rsquo;t agreed to. Nothing before the button below has
          charged you a penny. Pressing it means you agree to pay, and to our terms. Saying yes also
          covers:
        </p>
        <ul className="m-0 ps-6 space-y-2">
          <li>
            the difference at the till. If the shopping comes to more than the estimate, we take up
            to {money(storeConfig.receipts.tillExtraFloorPence)} more, or a fifth of the estimate if
            that is more. If it comes to less, we give the difference back to your card.
          </li>
          <li>
            if your basket is split between Runners, an extra Runner&rsquo;s delivery fee of{' '}
            {money(storeConfig.fees.extraRunnerDeliveryPence)} for each one.
          </li>
          <li>any plan you have chosen, which renews each month until you stop it.</li>
          <li>a booked or weekly order, which is charged on its date.</li>
        </ul>
        <p className="m-0">You can cancel free until your Runner starts shopping.</p>
        <Link to="/terms" className="control bg-paper/10 text-paper underline">
          Read our terms
        </Link>
      </section>

      {error !== '' && (
        <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
          {error}
        </p>
      )}
      {error.startsWith('Please add your card again') && (
        <Link to="/card" className="control border-paper bg-paper/10 text-paper">
          Add my card again
        </Link>
      )}

      {overMaximum && (
        <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
          This comes to about {money(pricing.goodsPence)} of shopping at the shop&rsquo;s prices,
          and one basket holds up to {money(storeConfig.fees.maximumBasketGoodsPence)}, brought by
          several Runners. Go back to your basket and take some things out.
        </p>
      )}

      <p className="m-0">
        <span className="visually-hidden">You are agreeing to this: </span>
        {statement}
      </p>
      <p className="m-0">
        By sending it, you ask us to start now. You lose the right to cancel once it is delivered.
      </p>

      {areaNews !== '' && (
        <p role="status" className="m-0">
          {areaNews}
        </p>
      )}

      <button
        ref={payButton}
        type="button"
        disabled={sending}
        onClick={() => {
          void onSend();
        }}
        className="control w-full bg-highlight text-on-accent text-display py-8 disabled:opacity-70"
      >
        {sending ? 'Sending your order…' : 'Send my order and pay'}
      </button>

      <Link to="/basket" className="control border-paper bg-paper/10 text-paper">
        Go back and change something
      </Link>

      {warningOpen && (
        <NoRunnerWarning
          onNews={setAreaNews}
          onClose={() => {
            setWarningOpen(false);
            // Back to the order: the button to pay is next.
            window.setTimeout(() => payButton.current?.focus(), 0);
          }}
        />
      )}
    </div>
  );
}
